<template>
  <div class="d-flex align-center">
    <div class="flex-grow-1 d-flex align-center flex-wrap gap15">

      <result-table-mode-selector
        :value="showGraphChart"
        @input="$emit('update:showGraphChart', $event)"
      />

      <div class="select-all-group d-flex align-center" v-if="!showGraphChart">
        <v-checkbox
          v-if="isMissionControlSession"
          class="wg-result-select-all-checkbox ma-0"
          color="success"
          off-icon="none"
          on-icon="check"
          :ripple="false"
          hide-details
          style="margin-left: -10px !important;"
          :disabled="selectAllDisabled"
          :input-value="selectAllChecked"
          :indeterminate="selectAllIndeterminate"
          @change="$emit('toggle-select-all')"
        >
          <template v-slot:label>
            <div class="d-flex align-center" style="text-wrap: nowrap; color: var(--v-blackColor-base); font-weight: 700;">
              <span>Select all</span>
              <span
                v-if="selectedCount > 0"
                class="wg-result-selected-count"
              >Selected ({{ selectedCount }})</span>
            </div>
          </template>
        </v-checkbox>
        <v-checkbox
          v-else-if="['user_editing'].includes(silo?.content_report_order?.order_status)"
          class="wg-result-select-all-checkbox ma-0"
          color="success"
          off-icon="none"
          on-icon="check"
          :ripple="false"
          hide-details
          style="margin-left: -10px !important;"
          :input-value="silo_keywords?.length !== 0 && selectedKeywordsForOrder?.length === silo_keywords?.length"
          @change="$emit('toggle-select-all-for-order')"
        >
          <template v-slot:label>
            <div class="d-flex align-center" style="text-wrap: nowrap; color: var(--v-blackColor-base); font-weight: 700;">
              <span>Select all</span>
              <span
                v-if="selectedKeywordsForOrder?.length || 0 > 0"
                class="wg-result-selected-count"
              >Selected ({{ selectedKeywordsForOrder?.length || 0 }})</span>
            </div>
          </template>
        </v-checkbox>
      </div>

    </div>
    <div class="d-flex align-center gap10">

      <div class="silo-advanced-metrics-toggle-row flex-shrink-0" v-if="!showGraphChart">
        <div class="silo-advanced-metrics-toggle-label">Advanced metrics</div>
        <v-switch
          inset
          hide-details
          dense
          color="#fff"
          :input-value="showAdvancedMetrics"
          @change="$emit('update:showAdvancedMetrics', $event)"
        />
      </div>

      <v-text-field
        class="table-search-field"
        outlined dense hide-details
        label="Search keywords"
        prepend-inner-icon="search"
        style="max-width: 300px; margin-top: -3px;"
        :value="searchTerm"
        @input="$emit('update:searchTerm', $event)"
      />

      <b-dropdown
        v-if="!showGraphChart"
        class="columns-visibility-dropdown"
        append-to-body
        aria-role="menu"
        scrollable
        max-height="200"
        trap-focus
        position="is-bottom-left"
      >
        <template #trigger>
          <b-tooltip
            label="Show/hide columns"
            type="is-dark"
          >
            <v-btn outlined
              color="grayColor" class="button-with-icon text-none" style="min-width: initial; padding: 0 8px;"
            >
              <i class="material-icons-outlined" style="margin-right:0; font-size: 1.2rem; color: var(--v-gray2Color-base);">view_week</i>
            </v-btn>
          </b-tooltip>
        </template>

        <b-dropdown-item custom paddingless aria-role="listitem">
          <div
            class="filter-item-label pb-4 mb-4"
            style="border-bottom: 2px solid #f5f5f5; font-weight: bold;"
          >{{hideableColumns.filter(column => isColumnVisible(column.key)).length}} out of {{hideableColumns.length}} selected</div>
          <div
            class="pb-4 mb-4"
            style="border-bottom: 2px solid #f5f5f5;"
          >
            <div
              v-for="hideableColumn in hideableColumns" :key="hideableColumn.key"
            >
              <v-checkbox
                :disabled="hideableColumn.isAdvancedMetric && !showAdvancedMetrics"
                color="#0075FF"
                dense
                hide-details
                :label="hideableColumn.label"
                :input-value="isColumnVisible(hideableColumn.key)"
                @change="(value) => setColumnVisibility(hideableColumn.key, value)"
              ></v-checkbox>
            </div>
          </div>

          <div class="d-flex align-center gap10">
            <v-btn text color="profileAnchorColor"
              class="text-none"
              @click="resetColumnVisibility"
            >
              <v-icon
                left
                dark
              >
                mdi-refresh
              </v-icon>
              Reset
            </v-btn>
          </div>
        </b-dropdown-item>
      </b-dropdown>

      <table-pdf-csv-download
        btnStyle="min-width: initial; padding: 0 8px;"
        btnLabel="Download"
        tooltipPosition="is-top"
        :iconOnly="true"
        tableId="silo-result-table table"
        fileName="silo-keywords"
        title="Silo Keywords"
        :columns="visibleTableColumns"
        :rows="sortedTableData"
        :useCustomGeneratePDF="true"
        @customGeneratePDF="generatePDF()"
      ></table-pdf-csv-download>

      <!-- POP Authority content plans are covered by the monthly package: no order CTAs -->
      <template v-if="isWhiteGloveUser && !isCalculating && !isPopAuthorityPlanView">
        <template v-if="isContentOrderEditable && !isContentOrderInCart">
          <v-btn
            v-if="selectedCount && !isMissionControlSession"
            rounded
            color="profileAnchorColor"
            class="silo-content-order-toolbar-btn button-with-icon text-none whiteColor--text"
            tag="a"
            :disabled="!isValidKeywordSelection?.(selectedSiloKeywordsRows)"
            @click="orderContentForSelectedKeywords()"
          >
            <i class="material-icons-outlined" style="margin-right: 10px;">shopping_basket</i>
            Order here ({{ selectedCount }})
          </v-btn>
          <v-tooltip
            v-else
            top
            color="transparent"
            :disabled="isKeywordSelectionValid"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                style="display: inline-block;"
                v-bind="attrs"
                v-on="on"
              >
                <v-btn
                  rounded
                  color="profileAnchorColor"
                  class="silo-content-order-toolbar-btn button-with-icon text-none whiteColor--text"
                  tag="a"
                  :disabled="!isKeywordSelectionValid"
                  @click="$emit('open-content-order-form')"
                >
                  <i class="material-icons-outlined" style="margin-right: 10px;">shopping_basket</i>
                  Order here <template v-if="selectedKeywordsForOrder?.length">({{ selectedKeywordsForOrder?.length }})</template>
                </v-btn>
              </div>
            </template>
            <div class="white-bg-tooltip small-tooltip">
              <span>To place the order, select the Top-level page and from 3 up to 15 supporting pages from the table below</span>
            </div>
          </v-tooltip>
        </template>

        <v-tooltip
          v-if="isContentOrderInCheckout"
          top
          color="transparent"
          :disabled="isKeywordSelectionValid"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              style="display: inline-block;"
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                rounded
                color="profileAnchorColor"
                class="silo-content-order-toolbar-btn button-with-icon text-none whiteColor--text"
                tag="a"
                :disabled="!(selectedKeywordsForOrder)"
                @click="$emit('open-content-order-form')"
              >
                <i class="material-icons-outlined" style="margin-right: 10px;">shopping_basket</i>
                Order ready for checkout - continue
              </v-btn>
            </div>
          </template>
          <div class="white-bg-tooltip small-tooltip">
            <span>To place the order, select the Top-level page and from 3 up to 15 supporting pages from the table below</span>
          </div>
        </v-tooltip>

        <v-tooltip
          v-if="isContentOrderInCart"
          top
          color="transparent"
          :disabled="isKeywordSelectionValid"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              style="display: inline-block;"
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                rounded
                color="profileAnchorColor"
                class="silo-content-order-toolbar-btn button-with-icon text-none whiteColor--text"
                tag="a"
                :disabled="!isKeywordSelectionValid"
                @click="$store.commit('setShowSiloContentReportsOrdersCart', true)"
              >
                <i class="material-icons-outlined" style="margin-right: 10px;">shopping_basket</i>
                Order in cart - continue
              </v-btn>
            </div>
          </template>
          <div class="white-bg-tooltip small-tooltip">
            <span>To place the order, select the Top-level page and from 3 up to 15 supporting pages from the table below</span>
          </div>
        </v-tooltip>
      </template>

      <b-tooltip
        v-if="deleted_silo_keywords?.length && !isQuickContentOrderView"
        label="Trash"
        type="is-dark"
        position="is-top"
      >
        <v-btn
          outlined
          color="grayColor"
          class="hw34"
          :style="'position: relative;' + (isDeletedView?'background-color:#bbcad1':'')"
          @click="$emit('update:isDeletedView', !isDeletedView)"
        >
          <i class="material-icons-outlined" style="font-size: 1.2rem; color: var(--v-gray2Color-base);">delete</i>
          <span class="deleted-view-count" v-if="deleted_silo_keywords?.length">{{ deleted_silo_keywords.length }}</span>
        </v-btn>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
import TablePdfCsvDownload from '@/components/Campaigns/TablePdfCsvDownload.vue';
import ResultTableModeSelector from '@/components/Campaigns/ResultTableModeSelector.vue';

export default {
  name: 'SiloResultTableControls',
  components: {
    TablePdfCsvDownload,
    ResultTableModeSelector,
  },
  computed: {
    /** POP Authority content plan (not a content-only order): the package covers the selection, nothing is ordered here. */
    isPopAuthorityPlanView () {
      return Boolean(this.$store.getters.isPopAuthorityUser)
        && !this.isQuickContentOrderView
        && !(this.silo && this.silo.isForQuickContentOrder)
    },
  },
  props: {
    silo_keywords: {
      type: Array,
      default: () => [],
    },
    selectKeywordForContentReportOrder: {
      type: Function,
      default: null,
    },
    unselectKeywordForContentReportOrder: {
      type: Function,
      default: null,
    },
    silo: {
      type: Object,
      default: null,
    },
    isWhiteGloveUser: {
      type: Boolean,
      default: false,
    },
    site: {
      type: Object,
      default: null,
    },
    isCalculating: {
      type: Boolean,
      default: false,
    },
    searchTerm: {
      type: String,
      default: '',
    },
    showAdvancedMetrics: {
      type: Boolean,
      default: true,
    },
    hideableColumns: {
      type: Array,
      default: () => [],
    },
    hiddenColumnsKeys: {
      type: Array,
      default: () => [],
    },
    isColumnVisible: {
      type: Function,
      required: true,
    },
    setColumnVisibility: {
      type: Function,
      required: true,
    },
    resetColumnVisibility: {
      type: Function,
      required: true,
    },
    showGraphChart: {
      type: Boolean,
      default: false,
    },
    visibleTableColumns: {
      type: Array,
      default: () => [],
    },
    sortedTableData: {
      type: Array,
      default: () => [],
    },
    generatePDF: {
      type: Function,
      required: true,
    },
    deleted_silo_keywords: {
      type: Array,
      default: () => [],
    },
    isQuickContentOrderView: {
      type: Boolean,
      default: false,
    },
    isDeletedView: {
      type: Boolean,
      default: false,
    },
    selectAllChecked: {
      type: Boolean,
      default: false,
    },
    selectAllIndeterminate: {
      type: Boolean,
      default: false,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    selectAllDisabled: {
      type: Boolean,
      default: false,
    },
    isContentOrderEditable: {
      type: Boolean,
      default: false,
    },
    isContentOrderInCheckout: {
      type: Boolean,
      default: false,
    },
    isContentOrderInCart: {
      type: Boolean,
      default: false,
    },
    selectedKeywordsForOrder: {
      type: Array,
      default: () => [],
    },
    isKeywordSelectionValid: {
      type: Boolean,
      default: false,
    },
    isMissionControlSession: {
      type: Boolean,
      default: false,
    },
    isValidKeywordSelection: {
      type: Function,
      default: null,
    },
    selectedSiloKeywordsRows: {
      type: Array,
      default: () => [],
    },
    orderContentForSelectedKeywords: {
      type: Function,
      default: null,
    },
  },
};
</script>

<style lang="scss" scoped>
.select-all-group {
  gap: 6px;
}

.wg-result-selected-count {
  margin-left: 8px;
  color: #ff5700;
  font-size: 0.875rem;
  font-weight: 500;
}

.silo-content-order-toolbar-btn {
  font-size: 0.938rem;
  font-weight: 700;
  height: 2.375rem !important;
  padding: 8px 12px !important;
}

::v-deep {
  .wg-result-select-all-checkbox {
    label {
      color: var(--v-mainColor-base);
      font-size: 0.9rem;
    }

    .v-input--selection-controls__input {
      border: 1px solid #999999 !important;
      border-radius: 4px;
      height: 20px !important;
      width: 20px !important;
    }

    &.v-input--is-label-active {
      .v-icon {
        font-size: 16px !important;
      }
    }
  }
}

.header-part {
  padding: 15px 20px;
}

.deleted-view-count {
  position: absolute;
  display: inline-block;
  top: -15px;
  right: -5px;
  background: #FF5700;
  color: #fff;
  width: 19px;
  height: 19px;
  line-height: 19px;
  text-align: center;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 700;
}

::v-deep .table-search-field {
  .v-icon {
    color: var(--v-gray36Color-base);
  }
  .v-label {
    color: var(--v-gray36Color-base);
    font-size: 0.85rem;
  }
  fieldset {
    border-color: var(--v-gray12Color-base);
  }
}

/* Same switch look as ContentOrderForm `.co-ai-toggle-row` */
.silo-advanced-metrics-toggle-row {
  display: flex;
  align-items: center;

  .silo-advanced-metrics-toggle-label {
    font-weight: 700;
    color: var(--v-blackColor-base, #1a1a1a);
    white-space: nowrap;
  }

  .v-input--switch {
    transform: scale(0.6);
  }

  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep {
    .v-input--selection-controls__ripple {
      color: #fff !important;
      caret-color: #fff !important;
    }

    .v-input--switch__thumb {
      width: 13px !important;
      height: 13px !important;
      top: calc(50% - 8px) !important;
    }

    .v-input--switch__track {
      opacity: 0.8 !important;
    }

    .v-input--is-label-active {
      .v-input--switch__track {
        opacity: 1 !important;
        color: #11a33d !important;
        caret-color: #11a33d !important;
      }
    }
  }
}
</style>

<style lang="scss">
.columns-visibility-dropdown {
  .dropdown-menu {
    padding-top: 17px;
  }
  .dropdown-content {
    border-radius: 5px;
    padding: 15px;
    width: 300px;
    border: 1px solid var(--v-grayColor-base);
    background: var(--v-whiteColor-base);
    box-shadow: 0px 2.563px 20.825px 0px rgba(0, 0, 0, 0.12);
  }
}
.material-icons {
  vertical-align: middle;
  font-size: 1rem;
}
</style>
